import { useAccount, usePublicClient } from "wagmi";
import { useQuery } from "@tanstack/react-query";
import { formatUnits } from "viem";
import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, WalletCards, Coins, Users, Clock } from "lucide-react";
import { PLATFORM_FEE_COLLECTOR } from "@/utils/referralSystem";
import { REFERRAL_REGISTRY_ADDRESS, referralRegistryAbi } from "@/utils/referralRegistry";

type Issuance = {
  codeHash: string;
  referrer: string;
  expiresAt: bigint;
  uses: number;
  totalPayout: bigint;
};

type Payout = {
  codeHash: string;
  referrer: string;
  token: string;
  amount: bigint;
  tx: string;
};

const shorten = (value: string) => `${value.slice(0, 6)}…${value.slice(-4)}`;

const ReferralDashboard = () => {
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const isAdmin = address?.toLowerCase() === PLATFORM_FEE_COLLECTOR.toLowerCase();

  const { data, isLoading } = useQuery({
    queryKey: ["referral-dashboard", REFERRAL_REGISTRY_ADDRESS, address, isAdmin],
    enabled: Boolean(publicClient && REFERRAL_REGISTRY_ADDRESS && address),
    queryFn: async () => {
      if (!publicClient || !REFERRAL_REGISTRY_ADDRESS || !address) {
        return { issuances: [] as Issuance[], payouts: [] as Payout[] };
      }

      const [issuanceLogs, attributionLogs, payoutLogs] = await Promise.all([
        publicClient.getLogs({ address: REFERRAL_REGISTRY_ADDRESS, event: referralRegistryAbi[5], fromBlock: 0n, toBlock: "latest" }),
        publicClient.getLogs({ address: REFERRAL_REGISTRY_ADDRESS, event: referralRegistryAbi[6], fromBlock: 0n, toBlock: "latest" }),
        publicClient.getLogs({ address: REFERRAL_REGISTRY_ADDRESS, event: referralRegistryAbi[7], fromBlock: 0n, toBlock: "latest" }),
      ]);

      const issuances = issuancesLogs
        .map((log: any) => ({
          codeHash: log.args.codeHash,
          referrer: log.args.referrer,
          expiresAt: log.args.expiresAt,
          uses: 0,
          totalPayout: 0n,
        }))
        .filter((item) => isAdmin || item.referrer?.toLowerCase() === address.toLowerCase());

      const registryPayouts = payoutLogs
        .map((log: any) => ({
          codeHash: log.args.codeHash,
          referrer: log.args.referrer,
          token: log.args.token,
          amount: log.args.amount,
          tx: log.transactionHash,
        }))
        .filter((item) => isAdmin || item.referrer?.toLowerCase() === address.toLowerCase());

      // The current ERC20 implementation emits a normal Transfer event for its
      // referral fee. Attribute those transfers to the referral as well as any
      // future ReferralPayout events emitted by registry-aware tokens.
      const tokenPayouts = (await Promise.all((attributionLogs as any[]).map(async (log) => {
        const token = log.args.token as `0x${string}`;
        const referrer = log.args.referrer as string;
        if (!isAdmin && referrer.toLowerCase() !== address.toLowerCase()) return [];
        const transferLogs = await publicClient.getLogs({
          address: token,
          event: { type: "event", name: "Transfer", inputs: [{ name: "from", type: "address", indexed: true }, { name: "to", type: "address", indexed: true }, { name: "value", type: "uint256", indexed: false }] },
          args: { to: referrer as `0x${string}` },
          fromBlock: log.blockNumber,
          toBlock: "latest",
        });
        return (transferLogs as any[]).map((transfer) => ({ codeHash: log.args.codeHash, referrer, token, amount: transfer.args.value as bigint, tx: transfer.transactionHash }));
      }))).flat();
      const payouts = [...registryPayouts, ...tokenPayouts];

      const payoutByCode = new Map<string, bigint>();
      for (const payout of payouts) payoutByCode.set(payout.codeHash, (payoutByCode.get(payout.codeHash) || 0n) + payout.amount);
      for (const issuance of issuances) issuance.totalPayout = payoutByCode.get(issuance.codeHash) || 0n;

      return { issuances, payouts };
    },
  });

  const issuances = data?.issuances || [];
  const payouts = data?.payouts || [];
  const totalPayouts = payouts.reduce((total, payout) => total + payout.amount, 0n);
  const activeCodes = issuances.filter((item) => item.expiresAt > BigInt(Math.floor(Date.now() / 1000))).length;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="max-w-6xl mx-auto space-y-8">
          <div>
            <h1 className="text-3xl font-bold">{isAdmin ? "Referral Analytics" : "My Referral Earnings"}</h1>
            <p className="text-muted-foreground mt-2">
              {isAdmin ? "Monitor every issued code, usage, and payout across the platform." : "Track your codes and the referral fees paid directly to your wallet."}
            </p>
          </div>

          {!address && <Card><CardContent className="pt-6">Connect your wallet to view referral earnings.</CardContent></Card>}
          {address && !REFERRAL_REGISTRY_ADDRESS && <Card><CardContent className="pt-6">Referral registry is not configured yet. Set VITE_REFERRAL_REGISTRY_ADDRESS after deploying the registry.</CardContent></Card>}
          {isLoading && <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>}

          {address && REFERRAL_REGISTRY_ADDRESS && !isLoading && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card><CardHeader><CardTitle className="text-sm flex gap-2"><WalletCards className="h-4 w-4" /> Codes issued</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{issuances.length}</CardContent></Card>
                <Card><CardHeader><CardTitle className="text-sm flex gap-2"><Clock className="h-4 w-4" /> Active codes</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{activeCodes}</CardContent></Card>
                <Card><CardHeader><CardTitle className="text-sm flex gap-2"><Users className="h-4 w-4" /> Token uses</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{payouts.length}</CardContent></Card>
                <Card><CardHeader><CardTitle className="text-sm flex gap-2"><Coins className="h-4 w-4" /> Fees paid</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{formatUnits(totalPayouts, 18)}</CardContent></Card>
              </div>

              <Card>
                <CardHeader><CardTitle>Issued referral codes</CardTitle></CardHeader>
                <CardContent>
                  {issuances.length === 0 ? <p className="text-muted-foreground">No referral codes have been issued to this wallet.</p> : <div className="space-y-3">{issuances.map((item) => <div key={item.codeHash} className="flex flex-wrap items-center justify-between gap-3 border rounded-lg p-4"><div><p className="font-mono">{shorten(item.codeHash)}</p><p className="text-sm text-muted-foreground">Referrer: {shorten(item.referrer)}</p></div><div className="flex items-center gap-3"><Badge variant={item.expiresAt > BigInt(Math.floor(Date.now() / 1000)) ? "default" : "secondary"}>{item.expiresAt > BigInt(Math.floor(Date.now() / 1000)) ? "Active" : "Expired"}</Badge><span className="text-sm">{formatUnits(item.totalPayout, 18)} paid</span></div></div>)}</div>}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Payout tracking</CardTitle></CardHeader>
                <CardContent>
                  {payouts.length === 0 ? <p className="text-muted-foreground">No on-chain referral payouts recorded yet.</p> : <div className="space-y-3">{payouts.map((payout) => <div key={`${payout.tx}-${payout.token}`} className="flex flex-wrap items-center justify-between gap-3 border rounded-lg p-4"><div><p className="font-mono">{shorten(payout.token)}</p><p className="text-sm text-muted-foreground">Code {shorten(payout.codeHash)} · tx {shorten(payout.tx)}</p></div><div className="text-right"><p className="font-semibold">{formatUnits(payout.amount, 18)} tokens</p><Badge>Paid on-chain</Badge></div></div>)}</div>}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ReferralDashboard;
